import { AppDispatch, RootState } from "@/redux"
import { Separator } from "@/components/ui/separator"
import { useSelector, useDispatch } from "react-redux"
import { FilterOrOptionType } from "@/constants/filters/filters"
import { InputFieldWrapper } from "../InputFieldsSection"
import { App } from "@/types/applications"
import { ActionSubjectOption } from "@/types/WorkflowCreate/AppConfiguration"
import { Button } from "@/components/ui/button"
import { ButtonVariant } from "@/constants/button/button"
import { workflowActions } from "@/redux/slice/workflowSlice"
import { JSONTextAreaOption } from "./JSONTextAreaOption"
import { useEffect, useState, useMemo, useRef } from "react"
import { DropdownOption } from "./DropdownOption"
import { getAppAvailableOptions } from "@/services/applicationsService"
import { AppOptions } from "@/types/WorkflowCreate/options"
import { WFCreateStepOption } from "@/types/WorkflowCreate/workflowCreate"
import { TextboxOption } from "./TextboxOption"

export const StepOptions = ({
  stepId,
  action,
  subject,
  credential,
  selectedOptions,
  optionsToFetch,
  app,
  order,
}: {
  stepId: string
  action: string,
  subject: string,
  credential: string,
  order: number
  selectedOptions: WFCreateStepOption[]
  optionsToFetch?: ActionSubjectOption[]
  app?: App,
  isEditing?: boolean,
}) => {
  const dispatch: AppDispatch = useDispatch()
  const { token } = useSelector((state: RootState) => state.user)
  const [optionModal, showOptionModal] = useState(false)
  const [options, setOptions] = useState<AppOptions[]>([])

  const lastFetchKey = useRef<string | null>(null);
  const stableOptionsToFetch = useMemo(() => optionsToFetch ?? [], [optionsToFetch]);
  const stableAppId = useMemo(() => app?.id, [app?.id]);
  const stableCredential = useMemo(() => credential, [credential]);
  const stableAction = useMemo(() => action, [action]);
  const stableSubject = useMemo(() => subject, [subject]);


  useEffect(() => {
    if (!token || !stableAction || !stableSubject || !stableCredential || !stableAppId || !stableOptionsToFetch.length) return;

    // Only fetch options whose dependencies are satisfied
    const optionsToActuallyFetch = stableOptionsToFetch.filter((option) => {
      const dependencies = option.dependsOn;
      if (!dependencies || dependencies.length === 0) return true;
      return dependencies.every(dep => {
        const selected = selectedOptions.find(so => so.option === dep.optionCode);
        if (!selected?.optionValue) return false;
        if (dep.optionTrueValues?.length) {
          return dep.optionTrueValues.includes(selected.optionValue);
        }
        return true;
      });
    });

    if (!optionsToActuallyFetch.length) return;

    const optionDependencies = selectedOptions
      .filter(s => s.option)
      .map(s => ({
        optionCode: s.option,
        value: s.optionValue
      }));
    const fetchKey = JSON.stringify({
      action: stableAction,
      subject: stableSubject,
      credential: stableCredential,
      options: optionsToActuallyFetch.map(o => o.code),
      optionDependencies: optionDependencies
    });
    if (lastFetchKey.current === fetchKey) return;
    lastFetchKey.current = fetchKey;

    const fetchOptions = async () => {
      const optionsResp = await dispatch(getAppAvailableOptions(
        token,
        stableAppId,
        stableCredential,
        stableAction,
        stableSubject,
        optionDependencies,
        optionsToActuallyFetch
      ));
      if (optionsResp) {
        setOptions(optionsResp);
      }
    };
    fetchOptions().catch((err) => console.log(err));
  }, [token, stableAction, stableSubject, stableCredential, stableAppId, dispatch, stableOptionsToFetch, selectedOptions]);

  const getSelectedChoice = (
    optionId: string,
  ) => {
    const selectedOption = selectedOptions.find(fo => fo.option === optionId)?.optionValue

    if (!selectedOption) {
      return undefined
    }

    const optionHasChoices = options?.find(f => f.code === optionId)?.optionValues;

    if (!optionHasChoices?.length) {
      return selectedOption
    }

    const selectedChoiceArray = selectedOption.split(', ');

    const parsedString = selectedChoiceArray.map(optionValue => {
      const option = optionHasChoices.find(fv => {
        if (typeof fv === 'number') {
          return String(fv) === optionValue;
        }
        return String(fv?.id) === optionValue;
      });

      return typeof option === 'number' ? option : option?.name;
    }).join(', ');

    if (parsedString.length === 0) {
      return selectedOption
    }

    return parsedString
  };

  const hasSelectedOption = (option: ActionSubjectOption): boolean => {
    const hasSelectedChoice = getSelectedChoice(option.code)
    return !!hasSelectedChoice
  }

  const getSelectChoices = (
    optionCode: string,
  ): { id: string; value: string; description?: string }[] => {
    const optionsValues = options?.find(o => o.code === optionCode)?.optionValues

    return optionsValues?.map((ov) => {
      if (typeof ov === 'object' && ov !== null && 'id' in ov && 'name' in ov) {
        return {
          id: String(ov.id),
          value: ov.name
        }
      }
      return { id: String(ov), value: String(ov) }
    }) ?? []
  }

  const clearOptionButtons = (option: ActionSubjectOption) => {
    return (
      <>
        <Button
          type="button"
          variant={ButtonVariant.OUTLINE}
          className="mr-3 font-semibold"
          disabled={!selectedOptions.find(so => so.option === option.code)?.optionValue}
          onClick={() => dispatch(workflowActions.removeStepOption({
            stepId,
            order,
            option: option.code
          }))}
        >
          Clear
        </Button>
        <Button
          type='button'
          className="w-auto font-semibold"
          variant={ButtonVariant.DEFAULT}
          onClick={() => showOptionModal(true)}
        >
          Update
        </Button>
      </>
    )
  }

  return optionsToFetch && optionsToFetch?.length > 0 && (
    <InputFieldWrapper key={app?.id} title="Options" subtitle='Use these options to process data according to your need'>
      <div className='my-4' />
      <div className="flex flex-col w-full" >
        {optionsToFetch?.filter(option => {
          // Only render if all dependencies are satisfied
          if (!option.dependsOn || option.dependsOn.length === 0) return true;
          return option.dependsOn.every(dep => {
            const selected = selectedOptions.find(so => so.option === dep.optionCode);
            if (!selected?.optionValue) return false;
            if (dep.optionTrueValues?.length) {
              return dep.optionTrueValues.includes(selected.optionValue);
            }
            return true;
          });
        }).map((option, i, arr) => {
          const selectedValue = selectedOptions?.find((so) => so.option === option?.code)?.optionValue
          const selectChoices = getSelectChoices(option.code)
          return (
            <div key={i + option.code} className="w-full">
              <div className="flex flex-row items-end justify-between w-full">
                <div className="flex flex-col items-start w-full mr-3">
                  <h3 className="text-primary font-semibold text-base mr-2">
                    {option.displayName}
                  </h3>
                  {option.description && <p className="text-secondary text-sm mb-2 text-left">{option.description}</p>}
                  <div
                    className={`w-full h-10 text-left rounded-md border-input bg-background px-3 py-2 text-sm flex items-center 
                  ${hasSelectedOption(option) ? 'text-primary' : 'text-secondary'}`}
                  >
                    <span className="w-full max-w-[440px] truncate">
                      {hasSelectedOption(option) ? getSelectedChoice(option.code) : 'Update'}
                    </span>
                  </div>
                </div>
                {option.type === FilterOrOptionType.DROPDOWN ? (
                  <DropdownOption
                    stepId={stepId}
                    code={option.code}
                    order={order}
                    selectOptions={selectChoices}
                    selectedValue={selectedValue}
                    name={option.displayName}
                    description={option.description}
                  />
                ) : null}
                {option.type === FilterOrOptionType.JSON ? (
                  <div className="flex flex-row">
                    {clearOptionButtons(option)}
                    <JSONTextAreaOption
                      stepId={stepId}
                      code={option.code}
                      order={order}
                      selectOptions={selectChoices}
                      selectedValue={selectedValue}
                      name={option.displayName}
                      description={option.description}
                      showOptionModal={showOptionModal}
                      optionModal={optionModal}
                    />
                  </div>
                ) : null}
                {option.type === FilterOrOptionType.TEXTBOX ? (
                  <div className="flex flex-row">
                    {clearOptionButtons(option)}
                    <TextboxOption
                      stepId={stepId}
                      code={option.code}
                      order={order}
                      selectOptions={selectChoices}
                      selectedValue={selectedValue}
                      name={option.displayName}
                      description={option.description}
                      showOptionModal={showOptionModal}
                      optionModal={optionModal}
                    />
                  </div>
                ) : null}
              </div>
              {i < arr.length - 1 && <Separator className="my-4" />}
            </div>
          )
        })}
      </div>
    </InputFieldWrapper>
  )
}
